// Instrument margins, shared by the server's alert severity and /app.
// Pure, no Node or browser APIs.

// The smallest excursion past the band that we will not attribute to the
// instrument. Set at roughly 3x typical kit precision, matching the
// reasoning already used for OUTLIER_FLOOR in server/db.js: real
// chemistry carries noise, and one reading inside that noise is not
// evidence of anything.
//
// Sources for the underlying instrument precision are the same hobby
// instruments the app already assumes: Hanna checkers (alk 0.1 dKH,
// phosphate 0.01 ppm), conductivity probes and refractometers
// (0.1 to 1.0 ppt), aquarium thermometers (0.5 F), and pH probes
// (0.05 to 0.1 between calibrations).
// Built from published ACCURACY specs at ~3x, not resolution — the
// reefer review (W7) caught the original table conflating the two:
// a Hanna dKH checker RESOLVES 0.1 but its stated accuracy is
// +/-0.3 dKH +/-5%, so 0.3 was 1x the error bar, not 3x.
export const INSTRUMENT_MARGIN = {
  alk: 0.6, // dKH, ~2-3x Hanna dKH checker accuracy (+/-0.3 +/-5%)
  calcium: 40, // ppm, ~3x titration step / Hanna Ca +/-6% at reef levels
  magnesium: 90, // ppm, 3x Salifert's 30 ppm resolution
  nitrate: 1.5, // ppm, colour-match kits are worse than this
  phosphate: 0.05, // ppm, ~3x Hanna ULR accuracy (+/-0.02 +/-5%)
  salinity: 0.6, // ppt, covers refractometer error outright
  temp: 1.5, // F, 3x a typical aquarium thermometer
  ph: 0.15, // pH, a probe drifts this much between calibrations
};

// Do two readings of one param agree, within about twice the margin? A
// slightly low 7.2 dKH does not confirm a typed 0.84: that pair is a
// typo. With no margin for the param there is nothing to judge by, so
// they agree.
export function readingsAgree(paramKey, a, b) {
  const margin = INSTRUMENT_MARGIN[paramKey];
  const x = Number(a);
  const y = Number(b);
  if (!margin || !Number.isFinite(x) || !Number.isFinite(y)) return true;
  return Math.abs(x - y) <= 2 * margin;
}
